<template>
  <div class="gammes-product row">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2 class="title-prod mb-0">{{ $t("home_client.featured_products.title") }}</h2>
      <div v-if="animalTypes.length > 0" class="d-flex gamme-list">
        <label
          v-for="animalType in animalTypes"
          class="round mx-2 px-4 py-1"
          :class="{ active: selectedAnimalTypes.includes(animalType.id) }"
          :key="animalType.id"
          @click.prevent="toggleAnimalType(animalType.id)"
        >
          {{ animalType.name }}
          <img v-if="selectedAnimalTypes.includes(animalType.id)" :src="iconchecked" />
        </label>
      </div>
    </div>
    <div class="col col-lg-11 col-md-11 col-sm-11">
      <div v-if="uniqueProducts.length > 0" class="carousel__wrapper">
        <Carousel v-bind="carouselConfig">
          <Slide v-for="product in uniqueProducts" :key="`incontournable-${product.id}`">
            <ProductCardSlide :product="normalizeProduct(product)" />
          </Slide>
          <template #addons>
            <Navigation />
          </template>
        </Carousel>
      </div>
      <div v-else-if="!isLoading" class="alert alert-info">
        <span v-if="selectedAnimalTypes.length > 0"> Aucun produit trouvé pour les filtres sélectionnés </span>
        <span v-else> Aucun produit trouvé </span>
      </div>
      <div v-else class="text-center">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Chargement...</span>
        </div>
      </div>
    </div>
    <h6 class="show-products text-center">
      <router-link to="/dashboard/products">{{ $t("gamme_product.see_all", "Voir tous les produits") }}</router-link>
    </h6>
  </div>
</template>

<script>
  import ProductCardSlide from "./products/ProductCardSlide.vue";
  import iconchecked from "../assets/checked.png";
  import "vue3-carousel/carousel.css";
  import { Carousel, Slide, Navigation } from "vue3-carousel";
  import { getIncontournables, normalizeIncontournablesProducts, normalizeAnimalTypes } from "../api/incontournables";

  export default {
    name: "IncontournablesProduct",
    components: {
      ProductCardSlide,
      Carousel,
      Slide,
      Navigation,
    },
    data() {
      return {
        iconchecked,
        products: [],
        animalTypes: [],
        selectedAnimalTypes: [],
        isLoading: false,
        error: null,
      };
    },
    computed: {
      uniqueProducts() {
        const seen = new Set();
        return this.products.filter(product => {
          if (seen.has(product.id)) return false;
          seen.add(product.id);
          return true;
        });
      },
      carouselConfig() {
        const shouldLoop = this.uniqueProducts.length >= 4;
        return {
          itemsToShow: 4,
          gap: 40,
          snapAlign: "start",
          autoplay: 4000,
          autoplayHoverPause: true,
          loop: shouldLoop,
          wrapAround: shouldLoop,
          pauseAutoplayOnHover: true,
          breakpoints: {
            0: { itemsToShow: 1, snapAlign: "start", loop: shouldLoop, wrapAround: shouldLoop },
            400: { itemsToShow: 2, snapAlign: "start", loop: shouldLoop, wrapAround: shouldLoop },
            600: { itemsToShow: 2, snapAlign: "start", loop: shouldLoop, wrapAround: shouldLoop },
            992: { itemsToShow: 4, snapAlign: "start", loop: shouldLoop, wrapAround: shouldLoop },
          },
        };
      },
    },
    methods: {
      normalizeProduct(product) {
        return {
          ...product,
          variant: product.variant || (product.variantsDetails && product.variantsDetails[0]) || {},
        };
      },
      async fetchIncontournables() {
        this.isLoading = true;
        this.error = null;

        try {
          const filters = {};
          if (this.selectedAnimalTypes.length > 0) {
            filters.animalTypes = this.selectedAnimalTypes;
          }
          const response = await getIncontournables(filters);
          if (response.filters && !this.animalTypes.length) {
            this.animalTypes = normalizeAnimalTypes(response.filters);
          }
          const normalizedProducts = normalizeIncontournablesProducts(response.items || []);
          this.products = normalizedProducts;
        } catch (error) {
          console.error("Erreur lors du chargement des produits incontournables:", error);
          this.error = "Impossible de charger les produits incontournables";
          this.products = [];
        } finally {
          this.isLoading = false;
        }
      },
      toggleAnimalType(animalTypeId) {
        const index = this.selectedAnimalTypes.indexOf(animalTypeId);
        if (index > -1) {
          this.selectedAnimalTypes.splice(index, 1);
        } else {
          this.selectedAnimalTypes.push(animalTypeId);
        }
        this.fetchIncontournables();
      },
    },
    async mounted() {
      await this.fetchIncontournables();
    },
  };
</script>

<style scoped>
  .gammes-product {
    gap: 10px;
    margin-top: 20px;
    justify-content: center;
  }

  .title-prod {
    color: #1e347d;
    font-weight: 900;
    line-height: 55px;
    font-size: 45px;
    font-style: normal;
    margin: 0;
    text-align: left;
  }

  .gamme-list {
    justify-content: flex-end;
    align-items: center;
  }

  .round {
    border: 1px solid #1e347d;
    border-radius: 76px;
    color: #1e347d;
    cursor: pointer;
  }

  .round.active {
    background: #1e347d;
    color: white;
  }

  @media (max-width: 768px) {
    .title-prod {
      font-size: 14px;
      line-height: 30px;
    }
  }

  .show-products a {
    color: #1e347d;
    font-size: 16px;
    font-weight: bold;
    text-decoration: underline !important;
  }

  .carousel {
    --vc-nav-background: rgba(255, 255, 255, 0.7);
    --vc-nav-border-radius: 100%;
  }

  .carousel__wrapper {
    padding: 2px;
    position: relative;
    margin: 0 auto;
  }

  :deep(.carousel__icon) {
    fill: #1e347d;
    height: 70%;
  }

  :deep(.carousel__next),
  :deep(.carousel__prev) {
    top: 38%;
    border: 1px solid #1e347d !important;
    background: #eff1f7;
  }

  :deep(.carousel__next:hover),
  :deep(.carousel__prev:hover) {
    background: #c9cfde;
  }

  :deep(.carousel__prev) {
    left: -4% !important;
  }

  :deep(.carousel__next) {
    right: -4% !important;
  }

  /* Responsive styles */
  @media (max-width: 992px) {
    .gammes-product {
      margin-top: 10px;
      gap: 6px;
      padding-left: 8px;
      padding-right: 8px;
    }
    .title-prod {
      font-size: 22px;
      line-height: 32px;
      margin-bottom: 10px;
    }
    .gamme-list {
      flex-wrap: wrap;
      gap: 6px;
      justify-content: flex-start;
    }
    .round {
      font-size: 13px;
      padding: 4px 12px;
    }
    .carousel__wrapper {
      padding: 0;
      max-width: 100vw;
    }
  }

  @media (max-width: 768px) {
    .gammes-product {
      margin-top: 4px;
      gap: 3px;
      padding-left: 2px;
      padding-right: 2px;
    }
    .title-prod {
      font-size: 14px;
      line-height: 30px;
    }
    .gamme-list {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      justify-content: flex-start;
    }
    .round {
      font-size: 11px;
      padding: 2px 6px;
    }
    .carousel__wrapper {
      padding: 0;
      max-width: 100vw;
    }
    .show-products a {
      font-size: 13px;
    }

    /* Pour mobile : titre et filtres en colonne */
    .d-flex.justify-content-between {
      flex-direction: column;
      align-items: flex-start;
      gap: 15px;
    }
  }

  @media (max-width: 600px) {
    .gammes-product {
      margin-top: 4px;
      gap: 3px;
      padding-left: 2px;
      padding-right: 2px;
    }
    .title-prod {
      font-size: 15px;
      line-height: 22px;
      margin-bottom: 6px;
    }
    .gamme-list {
      flex-wrap: wrap;
      gap: 10px;
      justify-content: flex-start;
    }
    .round {
      font-size: 11px;
      padding: 2px 6px;
    }
    .carousel__wrapper {
      padding: 0;
      max-width: 100vw;
    }
    .show-products a {
      font-size: 13px;
    }
  }
</style>
